import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { OrganizationHeader } from './_OrganizationHeader';
import { MembersSection } from './_MembersSection';
import { OrganizationModals } from './_OrganizationModals';
import { AccountProfileSection, type ProfileSettingsCopy } from './_AccountProfileSection';

interface Organization {
  id: string;
  name: string;
  slug: string;
  owner: {
    id: number;
    email: string;
  };
  members_count: number;
  is_owner: boolean;
  is_member: boolean;
  created_at: string;
  ecommerce: {
    id: number;
    name: string;
    slug: string;
  } | null;
}

interface Member {
  id: string;
  user: {
    id: number;
    name: string;
    email: string;
    avatar_url: string | null;
  };
  role: string;
  is_owner: boolean;
  joined_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  invited_by: {
    id: number;
    email: string;
  };
  expires_at: string;
  created_at: string;
}

interface Props {
  organization: Organization;
  members: Member[];
  pending_invitations: PendingInvitation[];
  profile_copy: ProfileSettingsCopy;
}

export default function Show({ organization, members, pending_invitations, profile_copy }: Props) {
  const { can_manage_organization } = usePermissions();

  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: number; email: string } | null>(null);
  const [showCancelInvitationModal, setShowCancelInvitationModal] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<{ id: string; email: string } | null>(null);
  const [showLeaveOrgModal, setShowLeaveOrgModal] = useState(false);

  const handleRemoveMember = (member: Member) => {
    setMemberToRemove({ id: member.user.id, email: member.user.email });
    setShowRemoveMemberModal(true);
  };

  const confirmRemoveMember = () => {
    if (memberToRemove) {
      router.delete(`/dashboard/organizations/${organization.slug}/remove_member`, {
        data: { user_id: memberToRemove.id }
      });
      setShowRemoveMemberModal(false);
      setMemberToRemove(null);
    }
  };

  const handleCancelInvitation = (invitation: PendingInvitation) => {
    setInvitationToCancel({ id: invitation.id, email: invitation.email });
    setShowCancelInvitationModal(true);
  };

  const confirmCancelInvitation = () => {
    if (invitationToCancel) {
      router.delete(`/dashboard/organizations/${organization.slug}/invitations/${invitationToCancel.id}`);
      setShowCancelInvitationModal(false);
      setInvitationToCancel(null);
    }
  };

  const handleChangeRole = (membershipId: string, newRole: string) => {
    router.patch(`/dashboard/organizations/${organization.slug}/change_role`, {
      membership_id: membershipId,
      role: newRole,
    });
  };

  return (
    <div className="space-y-6">
      <AccountProfileSection copy={profile_copy} />

      <OrganizationHeader
        organization={organization}
        canManage={can_manage_organization}
        onLeaveClick={() => setShowLeaveOrgModal(true)}
      />

      <MembersSection
        organizationSlug={organization.slug}
        members={members}
        pendingInvitations={pending_invitations}
        canManage={can_manage_organization}
        onRemoveMember={handleRemoveMember}
        onCancelInvitation={handleCancelInvitation}
        onChangeRole={handleChangeRole}
      />

      <OrganizationModals
        showRemoveMemberModal={showRemoveMemberModal}
        memberToRemove={memberToRemove}
        onCloseRemoveMember={() => { setShowRemoveMemberModal(false); setMemberToRemove(null); }}
        onConfirmRemoveMember={confirmRemoveMember}
        showCancelInvitationModal={showCancelInvitationModal}
        invitationToCancel={invitationToCancel}
        onCloseCancelInvitation={() => { setShowCancelInvitationModal(false); setInvitationToCancel(null); }}
        onConfirmCancelInvitation={confirmCancelInvitation}
        showLeaveOrgModal={showLeaveOrgModal}
        onCloseLeaveOrg={() => setShowLeaveOrgModal(false)}
        onConfirmLeaveOrg={() => {
          router.delete(`/dashboard/organizations/${organization.slug}/leave`);
          setShowLeaveOrgModal(false);
        }}
      />
    </div>
  );
}

Show.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>
